import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { resolveAnalyticsElement } from "@/lib/analytics/analyticsElement";
import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";

const adminLinkVariants = cva(
  "text-(--color-accent-strong) underline-offset-4 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-accent-strong)",
  {
    variants: {
      size: {
        // Inherit the surrounding text size (table cells, sentences).
        inherit: "",
        xs: "text-xs",
        sm: "text-sm",
        lg: "text-lg",
      },
      weight: {
        inherit: "",
        medium: "font-medium",
        semibold: "font-semibold",
      },
    },
    defaultVariants: {
      size: "inherit",
      weight: "inherit",
    },
  },
);

type AdminLinkProps = React.ComponentProps<"a"> &
  VariantProps<typeof adminLinkVariants> & {
    /**
     * Render the chrome onto the child element instead of an `<a>` — use it to
     * wrap `Link` / `InternalLink` / `ExternalLink` so routing and
     * analytics stay with the wrapped component.
     */
    asChild?: boolean;
    analyticsElementId?: AnalyticsElementId;
  };

/**
 * Canonical inline text link for admin surfaces — accent color, hover
 * underline, and a keyboard focus outline. The internal / generic sibling of
 * `AdminExternalLink` (which owns the trailing external-link icon and required
 * tracking). Use instead of hand-rolling
 * `text-(--color-accent-strong) underline-offset-4 hover:underline` anchors.
 */
const AdminLink = ({
  className,
  size,
  weight,
  asChild = false,
  analyticsElementId,
  onClick,
  ...props
}: AdminLinkProps) => {
  const Comp = asChild ? Slot : "a";
  const analytics = resolveAnalyticsElement(analyticsElementId, { flushOutboxOnClick: true });

  return (
    <Comp
      data-slot="admin-link"
      className={cn(adminLinkVariants({ size, weight }), className)}
      {...analytics?.attributes}
      onClick={(event) => {
        analytics?.emitClick();
        onClick?.(event);
      }}
      {...props}
    />
  );
};

export { AdminLink };
